import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { ADMIN } from "../constants/testIds";
import Logo from "../components/Logo";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../components/ui/table";
import { LogOut, Plus, Pencil, Trash2 } from "lucide-react";

export default function AdminDashboard() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const logout = () => {
    localStorage.removeItem("kl_token");
    localStorage.removeItem("kl_user");
    nav("/admin/login");
  };

  const load = async () => {
    try {
      const [meR, enqR, projR] = await Promise.all([
        api.get("/auth/me"),
        api.get("/admin/enquiries"),
        api.get("/projects"),
      ]);
      setMe(meR.data);
      setEnquiries(enqR.data);
      setProjects(projR.data);
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
      } else {
        toast.error("Failed to load");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("kl_token")) {
      nav("/admin/login");
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deleteProject = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    try {
      await api.delete(`/admin/projects/${id}`);
      toast.success("Project deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openCreate = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (p) => { setEditing(p); setDialogOpen(true); };

  if (loading) {
    return <div className="min-h-screen bg-editorial flex items-center justify-center chapter-num text-obsidian/50">LOADING STUDIO…</div>;
  }

  return (
    <div className="min-h-screen bg-editorial text-obsidian">
      <header className="border-b border-borderlt bg-editorial/85 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-[72px] flex items-center justify-between">
          <Link to="/"><Logo variant="dark" size={52} /></Link>
          <div className="flex items-center gap-6">
            <span className="chapter-num text-obsidian/60 hidden md:inline">{me?.email}</span>
            <Button data-testid={ADMIN.logout} variant="outline" size="sm" onClick={logout} className="border-obsidian/30">
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-14">
        <div className="chapter-num text-obsidian/60 mb-3">STUDIO · CONTROL</div>
        <h1 className="font-serif-display text-5xl md:text-6xl leading-none">Dashboard.</h1>

        <Tabs defaultValue="enquiries" className="mt-12">
          <TabsList className="bg-surface">
            <TabsTrigger data-testid={ADMIN.tabEnquiries} value="enquiries" className="chapter-num">
              ENQUIRIES · {enquiries.length}
            </TabsTrigger>
            <TabsTrigger data-testid={ADMIN.tabProjects} value="projects" className="chapter-num">
              PROJECTS · {projects.length}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="enquiries" className="mt-8">
            <div className="border border-borderlt bg-white">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Received</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Project</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {enquiries.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center py-14 text-obsidian/50">No enquiries yet.</TableCell></TableRow>
                  )}
                  {enquiries.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="chapter-num text-obsidian/60">{new Date(e.created_at).toLocaleDateString("en-GB")}</TableCell>
                      <TableCell className="font-medium">{e.name}</TableCell>
                      <TableCell>
                        <div className="text-sm">{e.email}</div>
                        <div className="text-xs text-obsidian/60">{e.phone}</div>
                      </TableCell>
                      <TableCell>{e.project || "—"}</TableCell>
                      <TableCell className="max-w-md truncate">{e.message}</TableCell>
                      <TableCell><Badge variant="secondary">{e.status}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="projects" className="mt-8">
            <div className="flex justify-end mb-4">
              <Button data-testid={ADMIN.createProject} onClick={openCreate} className="bg-obsidian text-editorial hover:bg-emerald_kl">
                <Plus className="w-4 h-4 mr-2" /> New Project
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((p) => (
                <div key={p.id} className="border border-borderlt bg-white overflow-hidden">
                  <div className="aspect-[4/3] bg-obsidian/5 overflow-hidden">
                    <img src={p.hero_image} alt={p.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-5">
                    <div className="chapter-num text-obsidian/50 mb-2">{p.status?.toUpperCase()} · {p.location}</div>
                    <h4 className="font-serif-display text-2xl leading-tight">{p.name}</h4>
                    <p className="text-sm text-obsidian/60 mt-1">{p.typology}</p>
                    <div className="mt-3 flex items-center gap-2">
                      <div className="flex-1 h-1 bg-obsidian/10">
                        <div className="h-full bg-emerald_kl" style={{ width: `${p.progress || 0}%` }} />
                      </div>
                      <span className="chapter-num">{p.progress || 0}%</span>
                    </div>
                    <div className="mt-4 flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openEdit(p)}><Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit</Button>
                      <Button size="sm" variant="outline" onClick={() => deleteProject(p.id)} className="text-destructive"><Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete</Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <ProjectDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        onSaved={load}
      />
    </div>
  );
}

function ProjectDialog({ open, onOpenChange, editing, onSaved }) {
  const empty = {
    name: "", location: "", category: "residential", status: "ongoing",
    typology: "", description: "", hero_image: "", progress: 0, year: new Date().getFullYear(),
    featured: false, gallery: [], highlights: [],
  };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(editing ? { ...empty, ...editing } : empty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, open]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form, progress: Number(form.progress) || 0, year: Number(form.year) || null };
      if (editing) {
        await api.put(`/admin/projects/${editing.id}`, body);
        toast.success("Project updated");
      } else {
        await api.post("/admin/projects", body);
        toast.success("Project created");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-3xl">{editing ? "Edit Project" : "New Project"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Name</Label><Input value={form.name} onChange={(e) => update("name", e.target.value)} /></div>
          <div><Label>Location</Label><Input value={form.location} onChange={(e) => update("location", e.target.value)} /></div>
          <div>
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => update("category", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="residential">Residential</SelectItem>
                <SelectItem value="commercial">Commercial</SelectItem>
                <SelectItem value="mixed">Mixed-use</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => update("status", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ongoing">Ongoing</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="upcoming">Upcoming</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Typology</Label><Input value={form.typology} onChange={(e) => update("typology", e.target.value)} /></div>
          <div><Label>Progress %</Label><Input type="number" min={0} max={100} value={form.progress} onChange={(e) => update("progress", e.target.value)} /></div>
          <div><Label>Year</Label><Input type="number" value={form.year} onChange={(e) => update("year", e.target.value)} /></div>
          <div className="md:col-span-2"><Label>Hero image URL</Label><Input value={form.hero_image} onChange={(e) => update("hero_image", e.target.value)} /></div>
          <div className="md:col-span-2"><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => update("description", e.target.value)} /></div>
          <label className="md:col-span-2 flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!form.featured} onChange={(e) => update("featured", e.target.checked)} />
            Featured on homepage
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-obsidian text-editorial hover:bg-emerald_kl">
            {saving ? "Saving…" : "Save Project"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
